import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BadgeCheck, ChevronDown } from "lucide-react";

// Iter-104 — Compact relative-time formatter for the "Reviewed X ago"
// freshness telemetry. Coarse bucketing (today / Nd / Nw / Nmo / Ny)
// is intentional — visitors care about "is this recent" at a glance,
// not exact days. Falls back to the raw ISO string on parse failure
// so we never strip a valid review timestamp.
function formatRelativeReview(iso) {
  if (!iso) return null;
  const reviewed = new Date(iso);
  if (Number.isNaN(reviewed.getTime())) return iso;
  const diffMs = Date.now() - reviewed.getTime();
  if (diffMs < 0) return iso;
  const day = 1000 * 60 * 60 * 24;
  const days = Math.floor(diffMs / day);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `${w} ${w === 1 ? "week" : "weeks"} ago`;
  }
  if (days < 365) {
    const m = Math.floor(days / 30);
    return `${m} ${m === 1 ? "month" : "months"} ago`;
  }
  const y = Math.floor(days / 365);
  return `${y} ${y === 1 ? "year" : "years"} ago`;
}

// Iter-103 — Shared "verified sources" pill + popover panel.
//
// Extracted from the Hero iter-102 implementation so the dark TrustBar
// strip can reuse the same trust signal pattern. One stronger trust
// signal beats N weak per-tile icons (Stripe/Ramp consolidation).
//
// Iter-104 — Panel rows now render "Reviewed N days/weeks/months ago"
// instead of the raw ISO date — much stronger editorial freshness
// signal at a glance.
//
// Props:
//   sources: Array<{ tileLabel: string, field: object, ready: boolean }>
//     Pre-resolved registry rows from the callsite (each callsite
//     calls `useDataField` for its keys, then passes results in).
//     Doing it this way keeps the rule-of-hooks happy and avoids any
//     hooks-inside-loop ambiguity.
//   tone:    "light" (default — emerald-on-light, for the hero on
//            #FAFAFA) or "dark" (emerald-on-dark, for the navy
//            TrustBar where text/divider colors flip).
//   testid:  unique data-testid prefix so multiple instances on the
//            same page don't collide.
// Iter-202 (Auto Loans audit) — `compactLabel` lets a caller replace
// the default "Verified · N source(s)" pill text with a stronger,
// source-naming label (e.g. "Verified · Fed G.19 + Experian") when a
// bare "Verified · 1 source" would read weak. The popover panel is
// unchanged — the full source list with links still opens on click.
export default function SourcesPopover({ sources, tone = "light", testid = "sources-pill", compactLabel = null }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const ready = (sources || []).filter((s) => s.ready && s.field);
  if (ready.length === 0) return null;

  const palette =
    tone === "dark"
      ? "text-[#10B981] hover:text-emerald-300"
      : "text-emerald-700 hover:text-emerald-800";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((s) => !s);
        }}
        aria-expanded={open}
        aria-label={`${ready.length} verified source${ready.length === 1 ? "" : "s"} — open details`}
        data-testid={testid}
        className={`inline-flex items-center gap-1 whitespace-nowrap transition-colors ${palette}`}
      >
        <BadgeCheck className="w-3 h-3" aria-hidden />
        <span>{compactLabel || <>Verified · {ready.length} source{ready.length === 1 ? "" : "s"}</>}</span>
        <ChevronDown
          className={`w-2.5 h-2.5 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <SourcesPanel
          anchorRef={triggerRef}
          onClose={() => setOpen(false)}
          sources={ready}
          testid={`${testid}-panel`}
        />
      )}
    </>
  );
}

function SourcesPanel({ anchorRef, onClose, sources, testid }) {
  const popoverRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });

  // Center below trigger; flip above when needed; clamp to viewport.
  useLayoutEffect(() => {
    if (!anchorRef.current) return;
    const place = () => {
      const a = anchorRef.current.getBoundingClientRect();
      const popW = 320;
      const popH = popoverRef.current?.offsetHeight || 240;
      const margin = 8;
      let top = a.bottom + 8;
      if (top + popH > window.innerHeight - margin) {
        top = Math.max(margin, a.top - popH - 8);
      }
      let left = a.left + a.width / 2 - popW / 2;
      left = Math.min(Math.max(margin, left), window.innerWidth - popW - margin);
      setCoords({ top, left, ready: true });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={popoverRef}
      role="tooltip"
      data-testid={testid}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        opacity: coords.ready ? 1 : 0,
        transition: "opacity 120ms ease-out",
      }}
      className="z-[1000] w-80 max-w-[calc(100vw-16px)] bg-slate-950 text-white p-4 shadow-2xl border border-slate-800"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#10B981] mb-3 inline-flex items-center gap-1.5">
        <BadgeCheck className="w-3 h-3" />
        Verified sources
      </p>
      <ul className="space-y-3">
        {sources.map((s) => (
          <li
            key={s.field.key || s.tileLabel}
            data-testid={`${testid}-row-${s.tileLabel.toLowerCase().replace(/\s+/g, "-")}`}
            className="text-xs leading-snug"
          >
            <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold mb-0.5">
              {s.tileLabel}
            </p>
            {s.field.source_name && (
              <p className="text-xs">
                {s.field.source_url ? (
                  <a
                    href={s.field.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-2 hover:underline"
                  >
                    {s.field.source_name}
                  </a>
                ) : (
                  s.field.source_name
                )}
                {s.field.source_year && (
                  <span className="text-slate-500"> · {s.field.source_year}</span>
                )}
              </p>
            )}
            {s.field.last_reviewed_at && (
              <p
                data-testid={`${testid}-row-${s.tileLabel.toLowerCase().replace(/\s+/g, "-")}-relative`}
                className="text-[10px] text-slate-500 mt-0.5"
                title={`Last reviewed ${s.field.last_reviewed_at}`}
              >
                Reviewed {formatRelativeReview(s.field.last_reviewed_at) || s.field.last_reviewed_at}
              </p>
            )}
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-end pt-3 mt-3 border-t border-slate-800">
        <a
          href="/methodology"
          data-testid={`${testid}-methodology`}
          className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#10B981] hover:text-emerald-300 underline-offset-2 hover:underline"
        >
          Methodology →
        </a>
      </div>
    </div>,
    document.body,
  );
}
